import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Menu } from 'antd';
import { ActionMenuIcon, EditAppointmentIcon, PlusIcon } from 'src/icons';
import { SuperAdminTable } from '../../../baseComponents/Table';
import { Button } from '../../../baseComponents';
import {
  getAllFormConditionsAction,
  getAllFormConditionsById,
  getAllFormsAction,
  getFormConditionsAction,
} from 'src/modules/SuperAdmin/store/actions/formconditions.action';
import { Sort } from 'src/icons/Sort.icon';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import AddNewFormConditions from './AddFormConditions';
import EditFormConditions from './EditFormConditions';
import FormConditionModal from './FormConditionModal';
import { getFromStorage } from '../../../../../../lib/storage';
import organizationEventEmitter from '../../../../../../utilities/organizationEmmiter';
import OrganizationFilter from '../OrganizationFilter';

const FormConditionsPage = () => {
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { getAllFormConditions } = useSelector(
    (state) => state?.formConditionsState
  );
  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );

  const { isLoading } = useSelector((state) => state?.conditionState);
  const [selectedOrganizationUUID, setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  const dispatch = useDispatch();
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setIsTableLoading] = useState(false);
  const [addFormConditionModalOpen, setIsAddFormConditionModalOpen] =
    useState(false);
  const [editFormConditionModalOpen, setIsEditFormConditionModalOpen] =
    useState(false);
  const [formConditionModalOpen, setIsFormConditionModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllFormConditionsAction(selectedOrganizationUUID));
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch, selectedOrganizationUUID]);

  useEffect(() => {
    setIsTableLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (
      getAllFormConditions &&
      Array.isArray(getAllFormConditions) &&
      getAllFormConditions?.length > 0
    ) {
      const dataArr = getAllFormConditions.map((item) => ({
        condition: item.condition,
        condition_id: item.condition_id,
        forms: item.forms,
      }));
      setTableData(dataArr);
    } else {
      setTableData([]);
    }
  }, [getAllFormConditions]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
      // setOrganizationOptions(dataArr);
    }
  }, [getActiveOrganizationsList]);

  const handleEditFormConditions = (id, record) => {
    dispatch(
      getAllFormConditionsById(
        id,
        record?.condition_id,
        setIsEditFormConditionModalOpen
      )
    );
    dispatch(
      getAllFormsAction(selectedOrganizationUUID, record?.condition_id, 'edit')
    );
  };

  const handleConditionModal = (form_id) => {
    dispatch(
      getFormConditionsAction(
        form_id,
        setIsFormConditionModalOpen,
        formConditionModalOpen
      )
    );
  };

  const menu_options = (id, record) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editFormConditions') {
            handleEditFormConditions(selectedOrganizationUUID, record);
          }
        }}
      >
        <Menu.Item key="editFormConditions">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit form conditions</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const condition_nameSorter = (a, b) => {
    return a.condition.localeCompare(b.condition);
  };

  const columns = [
    {
      title: (
        <span className="font-semibold">
          Condition{' '}
          {tableData?.length > 0 && <Sort className={'sort-icon-doctor'} />}
        </span>
      ),
      dataIndex: 'condition',
      key: 'condition',
      sorter: tableData?.length > 0 && condition_nameSorter,
    },
    {
      title: (
        <span className="font-semibold">
          Forms {/* You can add sorting icons if needed */}
        </span>
      ),
      dataIndex: 'forms',
      key: 'forms',
      render: (forms) => {
        // Simple hash function to generate a color from a string (form name)
        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          let color = '#';
          for (let i = 0; i < 3; i++) {
            const value = (hash >> (i * 8)) & 0xff;
            // Increase brightness or opacity of the color here
            const lightValue = Math.min(255, value + 30);
            color += ('00' + lightValue.toString(16)).substr(-2);
          }
          return color;
        };
        return (
          <div className="flex flex-wrap gap-2">
            {forms.map((form, index) => {
              const form_id = form?.form_id;
              const color = stringToColor(form?.form_type);
              return (
                <div
                  key={index}
                  className="flex items-center px-3 py-1 gap-1 mt-2 w-fit rounded-3xl cursor-pointer"
                  style={{ border: `2px solid ${color}` }}
                  onClick={() => {
                    handleConditionModal(form_id);
                  }}
                >
                  {form?.form_type}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',

      render: (text, record) => {
        return (
          <Dropdown
            className=""
            overlay={menu_options(record?.condition_id, record)}
          >
            <a className="" onClick={(e) => e.preventDefault()}>
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const handleFilterChange = useCallback(
    (option) => {
      setSelectedOrganizationUUID(option?.org_uuid);
      dispatch(getAllFormConditionsAction(option?.org_uuid));
    },
    [dispatch]
  );

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUUID(newOrg);
        handleFilterChange(newOrg);
      }
    );

    return () => unsubscribe();
  }, [handleFilterChange]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row lg:justify-between justify-center items-center">
        <div className="flex flex-col sm:flex-row">
          <div className="flex justify-start gap-12 ">
            <OrganizationFilter />
          </div>
        </div>
        <div className="flex justify-end items-end">
          <Button
            type={'button'}
            onClick={() => {
              setIsAddFormConditionModalOpen(true);
            }}
          >
            <PlusIcon className={`mr-2`} /> Add form condition
          </Button>
        </div>
      </div>
      <div>
        <div className="mt-5 ant-table rounded-xl shadow-xl">
          <SuperAdminTable
            className={'appointment-page-table'}
            data={tableData}
            columns={columns}
            loading={tableLoading}
            rowKey={(record, index) => index}
          />
        </div>
      </div>

      {/* All modals constructed here */}
      {addFormConditionModalOpen && (
        <AddNewFormConditions
          isOpen={addFormConditionModalOpen}
          setIsOpen={setIsAddFormConditionModalOpen}
          filterSetSelectedOrganizationUUID={setSelectedOrganizationUUID}
          selectedOrganizationUUID={selectedOrganizationUUID}
          selectedOrganizationName={
            getActiveOrganizationsList?.find(
              (org) => org?.org_uuid === selectedOrganizationUUID
            )?.org_name || null
          }
        />
      )}
      {editFormConditionModalOpen && (
        <EditFormConditions
          isOpen={editFormConditionModalOpen}
          setIsOpen={setIsEditFormConditionModalOpen}
          filterSelectedOrganizationUUID={selectedOrganizationUUID}
        />
      )}
      {formConditionModalOpen && (
        <FormConditionModal
          isOpen={formConditionModalOpen}
          setIsOpen={setIsFormConditionModalOpen}
        />
      )}
    </div>
  );
};

export default FormConditionsPage;
